import type { ReactNode } from 'react';
import styles from './Thinking.module.css';
import { CharacterAvatar } from './CharacterAvatar.js';
import { VisuallyHidden } from './VisuallyHidden.js';

/**
 * **返事を書いている間の合図**（§4.4 / §5.5 / §4.6 規約 6）。
 *
 * ## `Spinner` ではなく点にした理由
 *
 * §5.1 は「**無名の『処理中…』を禁じる**」と定めており、
 * `Spinner` は**工程名を必須**にしてある。
 * **ここで出せる工程名は無い** — §4.4 が「**工程名を出すと隠蔽が壊れる**」と
 * 書いているのは可視フェーズの外側の話だが、**通常ターンでも
 * 『Evidence を抽出しています』は学習者に見せる語ではない**（§5 の内部語）。
 *
 * **だから「エクマルが何か書いている」ことだけを形で示す。**
 * **これは工程の告知ではなく、発話の予告である** —
 * **場所を先に取る**ので、返事が届いたときに版面が飛ばない。
 *
 * ## 呼び出し側が守る規約
 *
 * **初回ターンの不可視フェーズには出さない**（§2.2 / §4.4）。
 * **`CharacterAvatar` の `thinking` も同じ規約を持つ**ので、
 * ここで両方が同時に破れる。**判断は `App` が 1 か所で持つ。**
 *
 * ## 読み上げ
 *
 * **点は読み上げに届かない**ので、`VisuallyHidden` に文を置く。
 * **`aria-live` は持たせない** — 出たり消えたりするたびに割り込む。
 */
export interface ThinkingProps {
  /** エクマルの原本 SVG（`?raw`） */
  readonly svg: string;
}

export function Thinking({ svg }: ThinkingProps): ReactNode {
  return (
    <div className={styles['row']}>
      <CharacterAvatar svg={svg} state="thinking" />
      <span className={styles['dots']} aria-hidden="true">
        <span className={styles['dot']} />
        <span className={styles['dot']} />
        <span className={styles['dot']} />
      </span>
      <VisuallyHidden>エクマルが返事を書いています</VisuallyHidden>
    </div>
  );
}
